import { useEffect, useState } from "react";

import { createReference } from "@medplum/core";
import { Encounter, QuestionnaireResponse } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { joinReferenceString } from "@/utils/medplum";

import { createPhysicalExamInitialValues } from "../utils/physical-exams";
import { PhysicalExamForm } from "../zod-schemas/physical-exams";

export default function usePhysicalExamInitialValues<T extends PhysicalExamForm>(
  questionnaireId: string | undefined,
  encounter: Encounter | undefined,
  setValues: (values: T) => void,
  baseValues: T,
): [T, QuestionnaireResponse | undefined, boolean] {
  const medplum = useMedplum();
  const [initialValues, setInitialValues] = useState<T>(baseValues);
  const [isLoading, setIsLoading] = useState(true);
  const [questionnaireResponse, setQuestionnaireResponse] = useState<QuestionnaireResponse>();

  useEffect(() => {
    if (!encounter || !questionnaireId || !isLoading) return;
    (async () => {
      const encounterReference = createReference(encounter);
      const subjectReference = encounter.subject!.reference;

      medplum.invalidateSearches("QuestionnaireResponse");
      const questionnaireResponse = await medplum.searchOne("QuestionnaireResponse", {
        encounter: encounterReference.reference,
        subject: subjectReference,
        questionnaire: joinReferenceString("Questionnaire", questionnaireId),
        _sort: "-_lastUpdated",
      });
      if (questionnaireResponse?.status !== "stopped") {
        const initialValues = createPhysicalExamInitialValues(questionnaireResponse, baseValues);
        setInitialValues(initialValues);
        setValues(initialValues);
        setQuestionnaireResponse(questionnaireResponse);
      }
      setIsLoading(false);
    })();
  }, [medplum, encounter, questionnaireId, baseValues, setValues, isLoading]);

  return [initialValues, questionnaireResponse, isLoading];
}
